import { useCallback, useMemo } from "react";
import { TableCell, TableRow, Checkbox, Typography, Grid, Stack } from "@mzc-pdc/ui";
import TableCard from "src/components_v2/card/table-card";
import theme from "@styles/theme";
import menuService from "@services/menuService";
import { Link, useLocation } from "react-router-dom";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import convertUtcToLocal from "@cores/convertUtcToLocal";
import { Menu } from "@components_v2/menu";
import { IconMoreHoriz } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";
import useCheckPermission from "@hooks/useCheckPermission";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import { siteMode } from "@cores/siteMode";
import { OverflowTooltip } from "@components_v2/ellipsis-tooltip";

export const ListTableBodyCollections = ({
    data,
    columns,
    checked,
    selected,
    thumbnailCrop,
    hiddenCheckbox,
    isCustomMenu,
    draggable,
    selectedItemsDragging,
    onClickRow,
    onDoubleClickRow,
    onClickDownload,
    onChangeCheck,
    onToggleModal,
    onDragStartItem,
    onDragEndItem,
}) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);
    const isCms = siteMode.get() === "CMS";

    const COLLECTIONS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const onHandleClickCollectionRow = (e) => {
        e.stopPropagation();
        onChangeCheck(data, e.target.checked);
    };

    const collectionDetailPath = useMemo(() => {
        if (isCustomMenu) {
            return `${pathname.split("/search")[0]}/${data.id}`; //Note: 컬렉션 검색 페이지에서 상세로 넘어갈 경우 /search를 제거한 후 id를 붙여야 함
        }

        return isCms
            ? menuService.paths.library.assetsCollectionDetail(spaceId, projectId, data.id)
            : menuService.paths.assetsCollectionDetail(spaceId, projectId, data.id);
    }, [isCms, isCustomMenu]);

    const getValueByColumn = useCallback(
        (columnId) => {
            if (columnId === "name")
                return (
                    <Stack direction={"row"} pl={theme.spacing(1)} alignItems={"center"}>
                        <Checkbox
                            color={"greySecondary"}
                            checked={checked}
                            onChange={onHandleClickCollectionRow}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ mr: 1 }}
                            hidden={hiddenCheckbox}
                        />
                        <TableCard
                            thumbnail={
                                <CollectionThumbnail
                                    collection={data}
                                    size={"small"}
                                    variant={"table"}
                                    thumbnailCrop={thumbnailCrop}
                                />
                            }
                            name={{
                                name: (
                                    <OverflowTooltip title={data.name}>
                                        <Link
                                            style={{
                                                color: theme.palette.secondary[800],
                                                fontWeight: 500,
                                                // 잘림 여부를 이 요소에서 측정하므로 말줄임 스타일을 직접 지정한다.
                                                display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            to={collectionDetailPath}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Typography variant={"body1_500"} color={theme.palette.greySecondary.main}>
                                                {data.name}
                                            </Typography>
                                        </Link>
                                    </OverflowTooltip>
                                ),
                            }}
                            sx={{ checkboxWidth: 40 }}
                        />
                    </Stack>
                );
            if (columnId === "count")
                return (
                    <Grid item>
                        <BadgeCollection totalCount={data?.assetTotalCount} />
                    </Grid>
                );
            if (columnId === "createdAt")
                return (
                    <Grid>
                        <Typography variant={"body1_400"} component={"span"}>
                            {data.created.at ? convertUtcToLocal(data.created.at, "YYYY-MM-DD HH:mm") : "-"}
                            <br />
                            {` by ${data.created.by.name.slice(0, 10)}${
                                data.created.by.name.length >= 10 ? "..." : ""
                            } `}
                        </Typography>
                    </Grid>
                );
            if (columnId === "updatedAt")
                return (
                    <Grid>
                        <Typography variant={"body1_400"} component={"span"}>
                            {data.updated.at ? convertUtcToLocal(data.updated.at, "YYYY-MM-DD HH:mm") : "-"}
                            <br />
                            {` by ${data.updated.by.name.slice(0, 10)}${
                                data.updated.by.name.length >= 10 ? "..." : ""
                            } `}
                        </Typography>
                    </Grid>
                );

            return null;
        },
        [data, checked, onChangeCheck, spaceId, projectId],
    );

    const menuList = useMemo(() => {
        if (isCms) return [];
        return [
            {
                label: t(`common::label::Share link`, `Delete`),
                onClick: () => {
                    onToggleModal("shareLink", true, data);
                },
                clickClose: true,
                disabled: !SHARES_PERMISSIONS.EXTERNAL,
            },
            {
                label: t(`common::label::Download`, `Download`),
                onClick: () => {
                    onClickDownload(data);
                },
                clickClose: true,
                disabled: !DOWNLOAD_PERMISSIONS.DOWNLOAD,
                divider: true,
            },
            {
                label: t(`common::label::Delete`, `Delete`),
                onClick: () => {
                    onToggleModal("delete", true, data);
                },
                clickClose: true,
                disabled: !COLLECTIONS_PERMISSIONS.EDIT,
                divider: true,
            },
            {
                label: t(`common::label::Archive`, `Archive`),
                onClick: () => {
                    onToggleModal("archive", true, data);
                },
                clickClose: true,
                disabled: !COLLECTIONS_PERMISSIONS.ARCHIVE,
                divider: true,
            },
        ];
    }, [isCms, onClickDownload]);

    const isRowDraggable = !!draggable && !!checked;

    return (
        <TableRow
            key={`collection-${data.id}`}
            hover
            role="checkbox"
            tabIndex={-1}
            draggable={isRowDraggable}
            onDragStart={isRowDraggable ? (e) => onDragStartItem?.(e, data) : undefined}
            onDragEnd={isRowDraggable ? onDragEndItem : undefined}
            onClick={(e) => {
                e.stopPropagation();
                if (isCms) return;
                onClickRow(data.id);
            }}
            onDoubleClick={() => {
                onDoubleClickRow(data.id);
            }}
            checked={checked}
            selected={selected}
            sx={{ height: "56px", opacity: selectedItemsDragging && !!checked ? 0.5 : 1 }}
        >
            {columns.map((column, index) => (
                <TableCell
                    key={column.id || index}
                    style={{
                        paddingLeft: column.id === "name" ? "0px" : undefined,
                        flex: column.id === "name" ? 1 : 0,
                    }}
                >
                    {getValueByColumn(column.id)}
                </TableCell>
            ))}
            <TableCell align="center">
                <Menu
                    id={"menu-button"}
                    menuWidth={160}
                    placement={"right"}
                    type={"icon"}
                    label={<IconMoreHoriz size={16} />}
                    color={"inherit"}
                    list={menuList}
                />
            </TableCell>
        </TableRow>
    );
};

export default ListTableBodyCollections;
